import { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router';
import { Search, Database, Plus, Check, X } from 'lucide-react';
import { ROLES, type PlanMatrizList } from '@sime/shared';
import { usePlanMatrizList } from '@/api/plan-matriz';
import { useAmbitos, AMBITOS_FALLBACK } from '@/hooks/use-ambitos';
import { useAuthStore } from '@/stores/auth.store';
import { NeuCard } from '@/components/ui/neu-card';
import { NeuButton } from '@/components/ui/neu-button';
import { NeuPagination } from '@/components/ui/neu-pagination';
import { NeuSelect } from '@/components/ui/neu-select';
import { useDebouncedValue } from '@/hooks/use-debounced-value';
import { cn } from '@/lib/utils';

export function PlanMatrizListPage() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const canCreate = user?.tipoUsuarioId === ROLES.ADMINISTRADOR;
  const { data: ambitosData } = useAmbitos();
  const ambitos = ambitosData ?? AMBITOS_FALLBACK;

  // Filter state
  const [search, setSearch] = useState('');
  const [ambitoFilter, setAmbitoFilter] = useState<number | undefined>(undefined);
  const [subcontratoFilter, setSubcontratoFilter] = useState<number | undefined>(undefined);
  const [activoFilter, setActivoFilter] = useState<boolean | undefined>(undefined);
  const [page, setPage] = useState(1);
  const limit = 20;

  // Debounce search input
  const debouncedSearch = useDebouncedValue(search, 300);

  const params: Partial<PlanMatrizList> = useMemo(() => ({
    page,
    limit,
    ambitoId: ambitoFilter,
    subcontratoId: subcontratoFilter,
    activo: activoFilter,
    search: debouncedSearch || undefined,
  }), [page, ambitoFilter, subcontratoFilter, activoFilter, debouncedSearch]);

  const { data, isLoading, isError } = usePlanMatrizList(params);

  const items = data?.data ?? [];
  const pag = data?.pagination;

  const handleRowClick = useCallback((id: number) => {
    void navigate(`/plan-matriz/${id}`);
  }, [navigate]);

  const handleRowKeyDown = useCallback((e: React.KeyboardEvent, id: number) => {
    if (e.key === 'Enter') void navigate(`/plan-matriz/${id}`);
  }, [navigate]);

  // Cycle through: undefined -> true -> false -> undefined
  const toggleActivoFilter = useCallback(() => {
    setActivoFilter((prev) => {
      if (prev === undefined) return true;
      if (prev) return false;
      return undefined;
    });
    setPage(1);
  }, []);

  return (
    <div className="max-w-[1200px] mx-auto px-4 md:px-6 lg:px-0">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 md:mb-8">
        <h1 className="text-2xl md:text-[34px] font-bold text-sime-text-primary tracking-tight-lg">
          Plan Matriz
        </h1>
        {canCreate ? (
          <NeuButton
            onClick={() => { void navigate('/plan-matriz/nuevo'); }}
            size="md"
          >
            <Plus className="h-4 w-4 mr-2" />
            Nuevo Plan Matriz
          </NeuButton>
        ) : null}
      </div>

      {/* Filters */}
      <NeuCard className="mb-4 md:mb-6">
        <div className="flex flex-col gap-3 md:flex-row md:flex-wrap md:items-center md:gap-4">
          {/* Search */}
          <div className="relative flex-1 min-w-0 md:min-w-[240px]">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-sime-text-tertiary pointer-events-none" />
            <input
              type="text"
              placeholder="Buscar por nombre o codigo..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className={cn(
                'w-full rounded-xl pl-10 pr-4 py-3 bg-transparent text-sm',
                'shadow-neu-btn-active',
                'focus:ring-2 focus:ring-sime-accent focus:outline-none',
                'placeholder:text-sime-text-tertiary text-sime-text-primary',
              )}
              aria-label="Buscar plan matriz"
            />
          </div>

          {/* Ambito filter */}
          <NeuSelect
            options={[
              { value: '', label: 'Todos los ambitos' },
              ...(user?.ambitoIds ?? []).map((id) => ({
                value: String(id),
                label: ambitos.find((a) => a.id === id)?.nombre ?? `Ambito ${id}`,
              })),
            ]}
            value={String(ambitoFilter ?? '')}
            onChange={(v) => { setAmbitoFilter(v ? Number(v) : undefined); setPage(1); }}
            aria-label="Filtrar por ambito"
            className="min-w-[160px]"
          />

          {/* Subcontrato filter */}
          <NeuSelect
            options={[{ value: '', label: 'Todos los subcontratos' }]}
            value={String(subcontratoFilter ?? '')}
            onChange={(v) => { setSubcontratoFilter(v ? Number(v) : undefined); setPage(1); }}
            aria-label="Filtrar por subcontrato"
            className="min-w-[160px]"
          />

          {/* Activo toggle */}
          <button
            type="button"
            onClick={toggleActivoFilter}
            className={cn(
              'flex items-center gap-2 rounded-xl px-4 py-3 text-sm font-medium transition-all duration-150',
              'shadow-neu-btn-active',
              activoFilter === true && 'text-sime-success',
              activoFilter === false && 'text-sime-danger',
              activoFilter === undefined && 'text-sime-text-secondary',
            )}
            aria-label={
              activoFilter === undefined
                ? 'Filtrar: mostrando todos'
                : activoFilter
                  ? 'Filtrar: mostrando solo activos'
                  : 'Filtrar: mostrando solo inactivos'
            }
          >
            {activoFilter === true && <Check className="h-4 w-4" />}
            {activoFilter === false && <X className="h-4 w-4" />}
            {activoFilter === undefined ? 'Todos' : activoFilter ? 'Activos' : 'Inactivos'}
          </button>
        </div>
      </NeuCard>

      {/* Mobile cards */}
      <div className="md:hidden space-y-3">
        {isLoading ? (
          Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="p-4 rounded-xl bg-sime-surface border border-sime-border-subtle animate-pulse">
              <div className="h-4 bg-sime-surface-raised rounded w-2/3 mb-3" />
              <div className="h-3 bg-sime-surface-raised rounded w-1/2 mb-2" />
              <div className="h-3 bg-sime-surface-raised rounded w-1/3" />
            </div>
          ))
        ) : isError ? (
          <div className="p-6 text-center text-sime-text-tertiary">
            Error al cargar el plan matriz. Intente nuevamente.
          </div>
        ) : items.length === 0 ? (
          <div className="p-6 text-center">
            <Database className="h-10 w-10 mx-auto mb-3 text-sime-text-tertiary" />
            <p className="text-sime-text-secondary font-medium">No se encontraron registros</p>
          </div>
        ) : (
          items.map((item) => (
            <div
              key={item.id}
              onClick={() => { handleRowClick(item.id); }}
              onKeyDown={(e) => { handleRowKeyDown(e, item.id); }}
              className="p-4 rounded-xl bg-sime-surface border border-sime-border-subtle active:bg-sime-surface-raised transition-colors cursor-pointer"
              role="link"
              tabIndex={0}
            >
              <div className="flex items-start justify-between mb-2">
                <span className="font-medium text-sime-text-primary line-clamp-2 mr-2">{item.nombre}</span>
                {item.activo ? (
                  <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-sime-success/15 text-sime-success shrink-0">
                    <Check className="h-3.5 w-3.5" />
                  </span>
                ) : (
                  <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-sime-danger/15 text-sime-danger shrink-0">
                    <X className="h-3.5 w-3.5" />
                  </span>
                )}
              </div>
              <div className="text-sm text-sime-text-secondary space-y-1">
                {item.codigo ? <p className="font-mono text-xs">{item.codigo}</p> : null}
                <div className="flex justify-between">
                  <span>{item.ambito?.nombre ?? '-'}</span>
                  <span>{item.frecuencia ?? '-'}</span>
                </div>
                <div className="flex justify-between text-xs text-sime-text-tertiary">
                  <span>{item.subcontrato?.nombre ?? '-'}</span>
                  <span>{(item._count?.ots ?? 0)} OTs</span>
                  <span>{(item._count?.aliases ?? 0)} aliases</span>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Desktop table */}
      <div className="hidden md:block rounded-2xl bg-sime-surface overflow-hidden border border-sime-border-subtle shadow-neu-raised">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-sime-border-subtle bg-sime-surface-raised">
              <th className="text-left px-6 py-4 font-semibold text-sime-text-secondary">
                Nombre
              </th>
              <th className="text-left px-6 py-4 font-semibold text-sime-text-secondary">
                Codigo
              </th>
              <th className="text-left px-6 py-4 font-semibold text-sime-text-secondary">
                Ambito
              </th>
              <th className="text-left px-6 py-4 font-semibold text-sime-text-secondary">
                Subcontrato
              </th>
              <th className="text-left px-6 py-4 font-semibold text-sime-text-secondary">
                Frecuencia
              </th>
              <th className="text-center px-6 py-4 font-semibold text-sime-text-secondary">
                OTs
              </th>
              <th className="text-center px-6 py-4 font-semibold text-sime-text-secondary">
                Aliases
              </th>
              <th className="text-center px-6 py-4 font-semibold text-sime-text-secondary">
                Activo
              </th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              Array.from({ length: 8 }).map((_, i) => (
                <tr key={i} className="border-b border-sime-border-subtle">
                  {Array.from({ length: 8 }).map((_, j) => (
                    <td key={j} className="px-6 py-4">
                      <div className="h-4 bg-sime-surface-raised rounded animate-pulse" />
                    </td>
                  ))}
                </tr>
              ))
            ) : isError ? (
              <tr>
                <td colSpan={8} className="px-6 py-12 text-center text-sime-text-tertiary">
                  Error al cargar el plan matriz. Intente nuevamente.
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-6 py-12 text-center">
                  <Database className="h-10 w-10 mx-auto mb-3 text-sime-text-tertiary" />
                  <p className="text-sime-text-secondary font-medium">
                    No se encontraron registros
                  </p>
                  <p className="text-sime-text-tertiary text-xs mt-1">
                    Ajuste los filtros de busqueda o agregue un nuevo plan matriz.
                  </p>
                </td>
              </tr>
            ) : (
              items.map((item) => (
                <tr
                  key={item.id}
                  onClick={() => { handleRowClick(item.id); }}
                  onKeyDown={(e) => { handleRowKeyDown(e, item.id); }}
                  className="border-b border-sime-border-subtle hover:bg-sime-surface-raised transition-colors cursor-pointer"
                  role="link"
                  tabIndex={0}
                >
                  <td className="px-6 py-4 font-medium text-sime-text-primary max-w-[300px] truncate">
                    {item.nombre}
                  </td>
                  <td className="px-6 py-4 text-sime-text-secondary font-mono text-xs">
                    {item.codigo ?? '-'}
                  </td>
                  <td className="px-6 py-4 text-sime-text-secondary">
                    {item.ambito?.nombre ?? '-'}
                  </td>
                  <td className="px-6 py-4 text-sime-text-secondary">
                    {item.subcontrato?.nombre ?? '-'}
                  </td>
                  <td className="px-6 py-4 text-sime-text-secondary">
                    {item.frecuencia ?? '-'}
                  </td>
                  <td className="px-6 py-4 text-center">
                    {(item._count?.ots ?? 0) > 0 ? (
                      <span className="inline-flex items-center justify-center min-w-[24px] h-6 px-2 rounded-full text-xs font-semibold bg-sime-success/10 text-sime-success">
                        {item._count?.ots}
                      </span>
                    ) : (
                      <span className="text-sime-text-tertiary">0</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-center">
                    {(item._count?.aliases ?? 0) > 0 ? (
                      <span className="inline-flex items-center justify-center min-w-[24px] h-6 px-2 rounded-full text-xs font-semibold bg-sime-accent/10 text-sime-accent">
                        {item._count?.aliases}
                      </span>
                    ) : (
                      <span className="text-sime-text-tertiary">0</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-center">
                    {item.activo ? (
                      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-sime-success/15 text-sime-success">
                        <Check className="h-3.5 w-3.5" />
                      </span>
                    ) : (
                      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-sime-danger/15 text-sime-danger">
                        <X className="h-3.5 w-3.5" />
                      </span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {/* Pagination */}
        {pag ? (
          <div className="px-4 md:px-6 py-4 border-t border-sime-border-subtle">
            <NeuPagination
              page={page}
              totalPages={pag.totalPages}
              onPageChange={setPage}
              summary={`${pag.total} registro${pag.total !== 1 ? 's' : ''} en total`}
            />
          </div>
        ) : null}
      </div>

      {/* Mobile pagination */}
      {pag ? (
        <div className="md:hidden px-4 py-4 mt-3">
          <NeuPagination
            page={page}
            totalPages={pag.totalPages}
            onPageChange={setPage}
            summary={`${pag.total} registro${pag.total !== 1 ? 's' : ''} en total`}
          />
        </div>
      ) : null}
    </div>
  );
}
